import { useReducer, useCallback, useRef } from 'react';
import type {
  ThinkingItem,
  FileInfo,
  WorkflowNodeInfo,
  WorkflowTrace,
  WorkflowNodeEvent,
  StreamingMessageState,
  FinalMessagePayload
} from '../types/contentBlock';

// ─── Action 类型 ─────────────────────

type StreamingAction =
  | { readonly type: 'RESET' }
  | { readonly type: 'START' }
  | { readonly type: 'TOKEN'; readonly delta: string; readonly fullContent: string }
  | { readonly type: 'THINKING'; readonly delta: string; readonly thinkingId: string; readonly tool?: string }
  | { readonly type: 'FILE'; readonly file: FileInfo }
  | { readonly type: 'WORKFLOW_TRACE'; readonly trace: WorkflowNodeEvent }
  | { readonly type: 'DONE'; readonly conversationId?: string; readonly message?: FinalMessagePayload }
  | { readonly type: 'ERROR'; readonly message: string };

// ─── 初始状态 ─────────────────────

const INITIAL_STATE: StreamingMessageState = {
  content: '',
  thinkingItems: [],
  files: [],
  workflowTrace: null,
  status: 'idle'
};

// ─── Reducer ─────────────────────

/**
 * 工作流追踪 reducer 辅助函数
 *
 * 根据 WorkflowNodeEvent 类型更新 WorkflowTrace 状态
 */
const reduceWorkflowTrace = (
  current: WorkflowTrace | null,
  event: WorkflowNodeEvent
): WorkflowTrace => {
  switch (event.type) {
    case 'workflow_started':
      return {
        workflowRunId: event.workflowRunId,
        status: 'running',
        nodes: []
      };

    case 'node_started': {
      const nodes: readonly WorkflowNodeInfo[] = [
        ...(current?.nodes ?? []),
        {
          nodeId: event.nodeId,
          nodeType: event.nodeType,
          title: event.title,
          index: event.index,
          status: 'running'
        }
      ];
      return {
        ...(current ?? { workflowRunId: '', status: 'running' as const }),
        nodes
      };
    }

    case 'node_finished': {
      const nodes: readonly WorkflowNodeInfo[] = (current?.nodes ?? []).map(n =>
        n.nodeId === event.nodeId
          ? {
              ...n,
              status: event.status,
              outputs: event.outputs,
              error: event.error,
              elapsedTime: event.elapsedTime
            }
          : n
      );
      return {
        ...(current ?? { workflowRunId: '', status: 'running' as const }),
        nodes
      };
    }

    case 'workflow_finished':
      return {
        ...(current ?? { workflowRunId: event.workflowRunId, nodes: [] }),
        workflowRunId: event.workflowRunId,
        status: event.status,
        totalTokens: event.totalTokens,
        finishedAt: event.finishedAt,
        // ★ 保存完整的 workflow outputs
        outputs: event.outputs,
      };

    default:
      return current ?? { workflowRunId: '', status: 'running', nodes: [] };
  }
};

/**
 * 更新/追加 thinking item
 *
 * 同一个 thinkingId 的 delta 合并到同一条；不同 id 则新增一条
 */
const reduceThinking = (
  items: readonly ThinkingItem[],
  thinkingId: string,
  delta: string,
  tool?: string
): readonly ThinkingItem[] => {
  const existing = items.find(t => t.id === thinkingId);
  if (existing) {
    return items.map(t =>
      t.id === thinkingId
        ? { ...t, content: t.content + delta }
        : t
    );
  }
  return [
    ...items,
    { id: thinkingId, content: delta, tool }
  ];
};

const streamingReducer = (
  state: StreamingMessageState,
  action: StreamingAction
): StreamingMessageState => {
  switch (action.type) {
    case 'RESET':
      return INITIAL_STATE;

    case 'START':
      return { ...INITIAL_STATE, status: 'streaming' };

    case 'TOKEN':
      return {
        ...state,
        content: action.fullContent
      };

    case 'THINKING':
      return {
        ...state,
        thinkingItems: reduceThinking(
          state.thinkingItems,
          action.thinkingId,
          action.delta,
          action.tool
        )
      };

    case 'FILE':
      return {
        ...state,
        files: [...state.files, action.file]
      };

    case 'WORKFLOW_TRACE':
      return {
        ...state,
        workflowTrace: reduceWorkflowTrace(state.workflowTrace, action.trace)
      };

    case 'DONE': {
      // 如果 done 事件携带了完整消息，以它为准
      const msg = action.message;
      if (msg) {
        return {
          content: msg.content,
          thinkingItems: msg.thinking ? [...msg.thinking] : state.thinkingItems,
          files: msg.files ? [...msg.files] : state.files,
          workflowTrace: msg.workflow_trace ?? state.workflowTrace,
          status: 'completed',
          conversationId: action.conversationId ?? state.conversationId
        };
      }
      return {
        ...state,
        status: 'completed',
        conversationId: action.conversationId ?? state.conversationId
      };
    }

    case 'ERROR':
      return {
        ...state,
        status: 'error',
        errorMessage: action.message
      };

    default:
      return state;
  }
};

// ─── Hook ─────────────────────

export interface UseStreamingMessageReturn {
  /** 当前流式消息状态 */
  readonly state: StreamingMessageState;
  /** 分发 action */
  readonly dispatch: React.Dispatch<StreamingAction>;
  /** 快捷：获取 SSECallbacks（可直接传给 backendChatService） */
  readonly createCallbacks: (onDone?: () => void) => {
    readonly onToken: (delta: string, fullContent: string) => void;
    readonly onThinking: (delta: string, thinkingId: string, tool?: string) => void;
    readonly onFile: (file: FileInfo) => void;
    readonly onWorkflowTrace: (trace: WorkflowNodeEvent) => void;
    readonly onDone: (data: { conversationId?: string; totalLength: number; message?: FinalMessagePayload }) => void;
    readonly onError: (error: Error) => void;
  };
  /** 重置状态 */
  readonly reset: () => void;
  /** 开始流式接收 */
  readonly start: () => void;
}

export const useStreamingMessage = (): UseStreamingMessageReturn => {
  const [state, dispatch] = useReducer(streamingReducer, INITIAL_STATE);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const reset = useCallback((): void => {
    dispatchRef.current({ type: 'RESET' });
  }, []);

  const start = useCallback((): void => {
    dispatchRef.current({ type: 'START' });
  }, []);

  const createCallbacks = useCallback((onDoneExtra?: () => void) => ({
    onToken: (delta: string, fullContent: string) => {
      dispatchRef.current({ type: 'TOKEN', delta, fullContent });
    },
    onThinking: (delta: string, thinkingId: string, tool?: string) => {
      dispatchRef.current({ type: 'THINKING', delta, thinkingId, tool });
    },
    onFile: (file: FileInfo) => {
      dispatchRef.current({ type: 'FILE', file });
    },
    onWorkflowTrace: (trace: WorkflowNodeEvent) => {
      dispatchRef.current({ type: 'WORKFLOW_TRACE', trace });
    },
    onDone: (data: { conversationId?: string; totalLength: number; message?: FinalMessagePayload }) => {
      dispatchRef.current({
        type: 'DONE',
        conversationId: data.conversationId,
        message: data.message
      });
      onDoneExtra?.();
    },
    onError: (error: Error) => {
      dispatchRef.current({ type: 'ERROR', message: error.message });
    }
  }), []);

  return { state, dispatch, createCallbacks, reset, start };
};